import { Injectable, NgZone, inject, signal } from '@angular/core';

import { AuthService } from './auth.service';

const CHANNEL = 'mm_admin_session';
const IDLE_MS = 15 * 60 * 1000;
const WARN_MS = 60 * 1000;
/** Throttle backend heartbeats so mouse noise does not flood the API. */
const HEARTBEAT_MIN_MS = 60 * 1000;

type BusEvent = { type: 'WARN_OPEN' | 'STAY' | 'LOGOUT' };

/**
 * PCI idle watchdog for the admin console: local 15m timeout + backend activity heartbeats.
 */
@Injectable({ providedIn: 'root' })
export class SessionIdleService {
  private readonly auth = inject(AuthService);
  private readonly zone = inject(NgZone);

  readonly warningOpen = signal(false);
  readonly secondsLeft = signal(60);

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private warnTimer: ReturnType<typeof setInterval> | null = null;
  private channel: BroadcastChannel | null = null;
  private started = false;
  private lastHeartbeatMs = 0;

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }
    this.started = true;
    try {
      this.channel = new BroadcastChannel(CHANNEL);
      this.channel.onmessage = (ev: MessageEvent<BusEvent>) => this.onBus(ev.data);
    } catch {
      this.channel = null;
    }
    window.addEventListener('storage', this.onStorage);
    for (const evt of ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const) {
      window.addEventListener(evt, this.onActivity, { passive: true });
    }
    this.resetIdle();
    this.pingBackend(true);
  }

  stop(): void {
    this.started = false;
    this.clearTimers();
    this.warningOpen.set(false);
    window.removeEventListener('storage', this.onStorage);
    for (const evt of ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const) {
      window.removeEventListener(evt, this.onActivity);
    }
    this.channel?.close();
    this.channel = null;
  }

  stay(): void {
    this.broadcast({ type: 'STAY' });
    this.closeWarningAndReset(true);
  }

  leave(): void {
    this.broadcast({ type: 'LOGOUT' });
    this.forceLogout();
  }

  private readonly onActivity = (): void => {
    if (this.warningOpen()) {
      return;
    }
    this.resetIdle();
    this.pingBackend(false);
  };

  private readonly onStorage = (ev: StorageEvent): void => {
    if (ev.key !== CHANNEL || !ev.newValue) {
      return;
    }
    try {
      this.onBus(JSON.parse(ev.newValue) as BusEvent);
    } catch {
      /* ignore */
    }
  };

  private onBus(msg: BusEvent): void {
    this.zone.run(() => {
      if (msg.type === 'WARN_OPEN') {
        this.openWarning();
      } else if (msg.type === 'STAY') {
        this.closeWarningAndReset(true);
      } else if (msg.type === 'LOGOUT') {
        this.forceLogout();
      }
    });
  }

  private resetIdle(): void {
    this.clearTimers();
    this.idleTimer = setTimeout(() => {
      this.broadcast({ type: 'WARN_OPEN' });
      this.openWarning();
    }, IDLE_MS);
  }

  private openWarning(): void {
    if (this.warningOpen()) {
      return;
    }
    this.warningOpen.set(true);
    this.secondsLeft.set(Math.floor(WARN_MS / 1000));
    this.warnTimer = setInterval(() => {
      const next = this.secondsLeft() - 1;
      this.secondsLeft.set(next);
      if (next <= 0) {
        this.leave();
      }
    }, 1000);
  }

  private closeWarningAndReset(forceHeartbeat: boolean): void {
    this.warningOpen.set(false);
    this.clearTimers();
    this.resetIdle();
    this.pingBackend(forceHeartbeat);
  }

  private forceLogout(): void {
    this.clearTimers();
    this.warningOpen.set(false);
    this.stop();
    this.auth.logout();
  }

  private pingBackend(force: boolean): void {
    if (!this.auth.isAuthenticated()) {
      return;
    }
    const now = Date.now();
    if (!force && now - this.lastHeartbeatMs < HEARTBEAT_MIN_MS) {
      return;
    }
    this.lastHeartbeatMs = now;
    this.auth.pingSessionActivity();
  }

  private clearTimers(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.warnTimer) {
      clearInterval(this.warnTimer);
      this.warnTimer = null;
    }
  }

  private broadcast(msg: BusEvent): void {
    this.channel?.postMessage(msg);
    try {
      localStorage.setItem(CHANNEL, JSON.stringify({ ...msg, t: Date.now() }));
    } catch {
      /* ignore */
    }
  }
}
